import { useAuthStore } from '@/store/auth';

// API Configuration
const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] || '';

// Types
export interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  message?: string;
  details?: Record<string, unknown>;
}

export interface Facility {
  id: number;
  facilityId: string;
  name: string;
  isActive: boolean;
  maxLogos: number;
  requiresTransparency: boolean;
  notes?: string;
  customWidth: number;
  customHeight: number;
  maxFileSize: number;
  allowedFormats: string;
  // Locale
  timezone: string;
  sheetCount: number;
  sheetNames: string; // JSON array string
  pausedSheets: string; // JSON array string of paused sheet names
  // Schedule source
  scheduleSource: 'manual' | 'google_sheets' | 'ccm_api' | 'leagueapps_api' | 'generic_recurring' | 'curlops_api';
  googleSheetsUrl?: string | null;
  ccmApiUrl?: string | null;
  ccmApiKey?: string | null;
  leagueAppsApiKey?: string | null;
  leagueAppsSiteId?: number | null;
  leagueAppsLocationId?: number | null;
  leagueAppsSourceTz?: string | null;
  curlOpsSlug?: string | null;
  curlOpsApiKey?: string | null;
  curlOpsBaseUrl?: string | null;
  controlHubConfig?: ControlHubConfig | null;
  streamingPaused?: boolean;
  // Admin-only Broadcast Mode kill switch (auto-expiring). ISO string or null.
  broadcastModeUntil?: string | null;
  lastSheetsSync?: string | null;
  /** JSON list of rows the last sync could not import: [{row,sheet,event,start,reason}] */
  lastSyncIssues?: string | null;
  /** Non-secret "source is usable" flags — club managers never receive the keys. */
  ccmConfigured?: boolean;
  leagueAppsConfigured?: boolean;
  curlOpsConfigured?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GenericScheduleEntry {
  id: number;
  facilityId: string;
  dayOfWeek: number;   // 0=Sun..6=Sat
  startTime: string;   // "HH:MM"
  endTime: string;
  leagueName: string | null;
  sheetIds: string[];  // [] = all active sheets
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFacilityRequest {
  facilityId: string;
  name: string;
  isActive?: boolean;
  maxLogos?: number;
  requiresTransparency?: boolean;
  notes?: string;
  timezone?: string;
  sheetCount?: number;
  sheetNames?: string;
  scheduleSource?: 'manual' | 'google_sheets' | 'ccm_api' | 'leagueapps_api' | 'generic_recurring' | 'curlops_api';
  googleSheetsUrl?: string | null;
  ccmApiUrl?: string | null;
  ccmApiKey?: string | null;
  leagueAppsApiKey?: string | null;
  leagueAppsSiteId?: number | null;
  leagueAppsLocationId?: number | null;
  leagueAppsSourceTz?: string | null;
  curlOpsSlug?: string | null;
  curlOpsApiKey?: string | null;
  curlOpsBaseUrl?: string | null;
}

export interface UpdateFacilityRequest {
  name?: string;
  isActive?: boolean;
  maxLogos?: number;
  requiresTransparency?: boolean;
  notes?: string;
  customWidth?: number;
  customHeight?: number;
  maxFileSize?: number;
  allowedFormats?: string;
  timezone?: string;
  sheetCount?: number;
  sheetNames?: string;
  pausedSheets?: string;
  scheduleSource?: 'manual' | 'google_sheets' | 'ccm_api' | 'leagueapps_api' | 'generic_recurring' | 'curlops_api';
  googleSheetsUrl?: string | null;
  ccmApiUrl?: string | null;
  ccmApiKey?: string | null;
  leagueAppsApiKey?: string | null;
  leagueAppsSiteId?: number | null;
  leagueAppsLocationId?: number | null;
  leagueAppsSourceTz?: string | null;
  curlOpsSlug?: string | null;
  curlOpsApiKey?: string | null;
  curlOpsBaseUrl?: string | null;
  controlHubConfig?: ControlHubConfig | null;
  streamingPaused?: boolean;
  // Admin-only Broadcast Mode kill switch (auto-expiring). ISO string or null.
  broadcastModeUntil?: string | null;
}

export interface AgentSessionMapping {
  sheetId?: string;    // e.g. "A", "B" — optional for legacy rows
  sheetName: string;
  obsPort: number;
  streamKey: string;
  /** Absolute path to obs64.exe on the host machine (Windows portable install). */
  obsExePath?: string;
  /** When false, skip auto-launching OBS for this sheet on agent startup. Default: true. */
  obsEnabled?: boolean;
}

export interface AgentHost {
  id: number;
  facilityId: string;
  hostLabel: string;
  obsWebSocketUrl: string;
  obsWebSocketPassword?: string | null;
  pollIntervalSeconds: number;
  apiKey: string;
  apiKeyMasked: string;
  isActive: boolean;
  notes?: string | null;
  macAddress?: string | null;
  /** LAN address of this host's Local Control Panel, e.g. http://192.168.1.20:8090 */
  panelUrl?: string | null;
  sessionMappings: AgentSessionMapping[];
  createdAt: string;
  updatedAt: string;
}

export interface AgentHostRequest {
  hostLabel: string;
  obsWebSocketUrl: string;
  obsWebSocketPassword?: string | null;
  pollIntervalSeconds: number;
  isActive: boolean;
  notes?: string | null;
  macAddress?: string | null;
  panelUrl?: string | null;
  sessionMappings: AgentSessionMapping[];
}

// ── Onsite Control Hub / Dashboard ──────────────────────────────────────────
export interface ControlHubTab {
  id: string;
  label: string;
  url: string;
  embed: 'iframe' | 'link';
}
export interface ControlHubConfig {
  enabled: boolean;
  companionUrl?: string | null;
  companionEmbed?: 'iframe' | 'link';
  customTabs: ControlHubTab[];
}

/** @deprecated Use AgentHost — kept for SchedulesPage compat during transition */
export type AgentConfig = AgentHost;
/** @deprecated Use AgentHostRequest */
export type UpsertAgentConfigRequest = AgentHostRequest;

export interface LoginResponse {
  success: boolean;
  token: string;
  user: {
    id: string;
    role: 'admin' | 'facility';
    facilityId?: string;
  };
  message: string;
  expiresIn: string;
}

export interface FacilitiesListResponse {
  facilities: Facility[];
  /** How far ahead sheet-backed schedules are imported (backend SHEETS_WINDOW_DAYS). */
  scheduleWindowDays?: number;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface HealthResponse {
  status: string;
  timestamp: string;
  uptime: number;
  environment: string;
  database: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ── Event import plan (backend lib/eventImportPlan.ts) ──────────────────────
export type ImportField = 'start' | 'sheet' | 'event' | 'team1' | 'team2' | 'notes';
export interface ImportPlanRow { rowNumber: number; sheetId: string; start: string; end: string; eventName: string; team1Name: string | null; team2Name: string | null; notes: string | null }
export interface EventImportPlan {
  new: Array<{ row: ImportPlanRow; restoreId: number | null }>;
  changed: Array<{ id: number; row: ImportPlanRow; diffs: Array<{ field: ImportField; from: string | null; to: string | null }>; moved: boolean; notesOnly: boolean }>;
  unchanged: Array<{ id: number; row: ImportPlanRow }>;
  removed: Array<{ id: number; sheetId: string; start: string; label: string }>;
  kept: Array<{ id: number; sheetId: string; start: string; label: string; reason: 'edited' | 'deleted' | 'starting' | 'played'; status: string; sheetRow: ImportPlanRow | null; inSheet: boolean }>;
  problems: Array<{ rowNumber: number; reason: string }>;
  dragFill: Array<{ rowNumber: number; eventName: string; majority: string }>;
  underway: boolean;
  noValidRows: boolean;
  needs: { underwayAck: boolean; removeAck: boolean; dragFillAck: boolean };
  hash: string;
}

// API Error class
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: Record<string, unknown>,
    public data?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// HTTP Client
class HttpClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private getAuthHeaders(): Record<string, string> {
    const { token } = useAuthStore.getState();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    let data: unknown;
    if (isJson) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      const errorData = data as Record<string, unknown>;
      const message = (errorData?.['message'] as string) || (errorData?.['error'] as string) || `HTTP ${response.status}`;
      throw new ApiError(
        message,
        response.status,
        errorData?.['details'] as Record<string, unknown>,
        errorData // Pass the full error data so frontend can access templates array
      );
    }

    return data as T;
  }

  private buildUrl(endpoint: string): string {
    if (this.baseURL) {
      return `${this.baseURL}${endpoint}`;
    }
    
    // Handle different endpoint prefixes for production
    if (endpoint.startsWith('/api/') || endpoint.startsWith('/auth') || endpoint.startsWith('/admin') || endpoint.startsWith('/facilities') || endpoint.startsWith('/health')) {
      return endpoint; // These already have the correct prefix or don't need /api prefix
    }
    
    return `/api${endpoint}`;
  }

  async get<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
    const fullUrl = this.buildUrl(endpoint);
    const url = new URL(fullUrl, window.location.origin);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const headers = this.getAuthHeaders();
    const requestInit: RequestInit = {
      method: 'POST',
      headers: data ? headers : Object.fromEntries(
        Object.entries(headers).filter(([key]) => key !== 'Content-Type')
      ),
    };

    if (data) {
      requestInit.body = JSON.stringify(data);
    }

    const fullUrl = this.buildUrl(endpoint);
    const url = new URL(fullUrl, window.location.origin);
    const response = await fetch(url.toString(), requestInit);
    return this.handleResponse<T>(response);
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const requestInit: RequestInit = {
      method: 'PATCH',
      headers: this.getAuthHeaders(),
    };

    if (data) {
      requestInit.body = JSON.stringify(data);
    }

    const fullUrl = this.buildUrl(endpoint);
    const url = new URL(fullUrl, window.location.origin);
    const response = await fetch(url.toString(), requestInit);
    return this.handleResponse<T>(response);
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    const requestInit: RequestInit = {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    };

    if (data) {
      requestInit.body = JSON.stringify(data);
    }

    const fullUrl = this.buildUrl(endpoint);
    const url = new URL(fullUrl, window.location.origin);
    const response = await fetch(url.toString(), requestInit);
    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string): Promise<T> {
    const fullUrl = this.buildUrl(endpoint);
    const url = new URL(fullUrl, window.location.origin);
    const headers = this.getAuthHeaders();
    // Remove Content-Type header for DELETE requests (no body)
    const { 'Content-Type': _, ...headersWithoutContentType } = headers;

    const response = await fetch(url.toString(), {
      method: 'DELETE',
      headers: headersWithoutContentType,
    });

    return this.handleResponse<T>(response);
  }

  // Authenticated binary download (sends the Bearer token; returns the blob + server filename).
  async getBlob(endpoint: string): Promise<{ blob: Blob; filename: string | null }> {
    const fullUrl = this.buildUrl(endpoint);
    const url = new URL(fullUrl, window.location.origin);
    const headers = this.getAuthHeaders();
    delete headers['Content-Type'];
    const response = await fetch(url.toString(), { method: 'GET', headers });
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try { const j = await response.json(); message = (j?.message || j?.error) ?? message; } catch { /* non-json */ }
      throw new ApiError(message, response.status);
    }
    const cd = response.headers.get('content-disposition') || '';
    const match = cd.match(/filename="?([^"]+)"?/);
    return { blob: await response.blob(), filename: match?.[1] ?? null };
  }
}

// Create HTTP client instance
export const httpClient = new HttpClient(API_BASE_URL);

// User documentation (role-scoped guide downloads; requires login)
export const docsApi = {
  list: (): Promise<{ guides: Array<{ key: string; title: string }> }> =>
    httpClient.get('/api/docs'),
  // Authenticated fetch of a guide PDF as a blob (the token goes in the header, not the URL).
  fetchBlob: (key: string): Promise<{ blob: Blob; filename: string | null }> =>
    httpClient.getBlob(`/api/docs/${key}`),
};

// Auth API
export const authApi = {
  // Unified smart login
  login: (email: string, password: string): Promise<LoginResponse> =>
    httpClient.post('/api/auth/login', { email, password }),

  // Production admin login (kept for compatibility)
  loginAdmin: (email: string, password: string): Promise<LoginResponse> =>
    httpClient.post('/api/auth/admin/login', { email, password }),

  // Production facility login (kept for compatibility)
  loginFacility: (facilityId: string, email: string, password: string): Promise<LoginResponse> =>
    httpClient.post('/api/auth/facility/login', { facilityId, email, password }),

  // Demo logins (for development)
  loginAdminDemo: (): Promise<LoginResponse> =>
    httpClient.post('/api/auth/demo/admin'),

  loginFacilityDemo: (facilityId: string): Promise<LoginResponse> =>
    httpClient.post('/api/auth/demo/facility', { facilityId }),

  validateToken: (): Promise<{ valid: boolean; user: { id: string; role: 'admin' | 'facility'; facilityId?: string } }> =>
    httpClient.get('/api/auth/validate'),

  // Self-service password change for the logged-in admin (under /api/admin —
  // /api/auth/* skips JWT verification, so this authenticated route lives there)
  changePassword: (currentPassword: string, newPassword: string): Promise<{ success: boolean }> =>
    httpClient.post('/api/admin/change-password', { currentPassword, newPassword }),
};

// Agent Hosts API (multi-host support)
export const agentHostApi = {
  list: (facilityId: string): Promise<{ hosts: AgentHost[] }> =>
    httpClient.get(`/api/admin/facilities/${facilityId}/agent-hosts`),

  create: (facilityId: string, data: AgentHostRequest): Promise<{ host: AgentHost }> =>
    httpClient.post(`/api/admin/facilities/${facilityId}/agent-hosts`, data),

  update: (facilityId: string, hostId: number, data: AgentHostRequest): Promise<{ host: AgentHost }> =>
    httpClient.put(`/api/admin/facilities/${facilityId}/agent-hosts/${hostId}`, data),

  remove: (facilityId: string, hostId: number): Promise<{ ok: boolean }> =>
    httpClient.delete(`/api/admin/facilities/${facilityId}/agent-hosts/${hostId}`),

  regenerateKey: (facilityId: string, hostId: number): Promise<{ host: AgentHost }> =>
    httpClient.post(`/api/admin/facilities/${facilityId}/agent-hosts/${hostId}/regenerate-key`, {}),
};

/** @deprecated Use agentHostApi — kept for SchedulesPage compat during transition */
export const agentConfigApi = {
  get: (facilityId: string): Promise<{ hosts: AgentHost[] }> =>
    agentHostApi.list(facilityId).then(r => ({ hosts: r.hosts })),
  upsert: (facilityId: string, data: UpsertAgentConfigRequest): Promise<{ config: AgentHost }> =>
    agentHostApi.create(facilityId, data).then(r => ({ config: r.host })),
  regenerateKey: (facilityId: string, hostId: number): Promise<{ host: AgentHost }> =>
    agentHostApi.regenerateKey(facilityId, hostId),
};

// ── Dashboard Status Summary ──────────────────────────────────────────────────

export interface FacilityStatusSummary {
  id: number;
  facilityId: string;
  name: string;
  isActive: boolean;
  timezone: string;
  scheduleSource: 'manual' | 'google_sheets' | 'ccm_api' | 'leagueapps_api' | 'generic_recurring' | 'curlops_api';
  sheetCount: number;
  lastSheetsSync: string | null;
  alertsPausedUntil: string | null;
  broadcastModeUntil: string | null;
  compositionCount: number;
  publishedCount: number;
  slideshowCount: number;
  logoCount: number;
  youtubeConnected: boolean;
  youtubeChannelName: string | null;
  titleTemplate: string | null;
  agentOnline: boolean;
  agentLastSeen: string | null;
  agentHostCount: number;
  agentVersion: string | null;
  latestAgentVersion: string | null;
  activeStreamCount: number;
  pausedSheetCount: number;
  upcomingScheduleCount: number;
}

export const dashboardApi = {
  facilityStatus: (): Promise<{ facilities: FacilityStatusSummary[] }> =>
    httpClient.get('/api/admin/dashboard-status'),
  // Pause/resume Discord alerts for a facility. pausedUntil = ISO string (future =
  // muted until then; far-future = indefinite) or null to resume. Admin only.
  setAlertsPause: (facilityId: string, pausedUntil: string | null): Promise<{ facilityId: string; alertsPausedUntil: string | null }> =>
    httpClient.put(`/api/admin/facilities/${facilityId}/alerts-pause`, { pausedUntil }),

  // The site's own Discord channel for copies of its alerts. Admin only; the URL
  // is a secret and only ever comes back masked (preview = last 6 characters).
  getAlertChannel: (facilityId: string): Promise<AlertChannelStatus> =>
    httpClient.get(`/api/admin/facilities/${facilityId}/alert-channel`),
  setAlertChannel: (facilityId: string, discordWebhookUrl: string): Promise<AlertChannelStatus> =>
    httpClient.put(`/api/admin/facilities/${facilityId}/alert-channel`, { discordWebhookUrl }),
  clearAlertChannel: (facilityId: string): Promise<AlertChannelStatus> =>
    httpClient.delete(`/api/admin/facilities/${facilityId}/alert-channel`),
  testAlertChannel: (facilityId: string): Promise<{ ok: true }> =>
    httpClient.post(`/api/admin/facilities/${facilityId}/alert-channel/test`, {}),

  // Camera inventory (1.32.0): each sheet's OBS camera setup as the agent last
  // read it, plus the last known good one. Admin only.
  getCameraInventory: (facilityId: string): Promise<{ facilityId: string; sheets: CameraInventorySheet[] }> =>
    httpClient.get(`/api/admin/camera-inventory/${encodeURIComponent(facilityId)}`),
  // The restore record (JSON) for one site, or every site when facilityId is omitted.
  exportCameraInventory: (facilityId?: string): Promise<unknown> =>
    httpClient.get(`/api/admin/camera-inventory/export${facilityId ? `?facilityId=${encodeURIComponent(facilityId)}` : ''}`),

  // Enable/end Broadcast Mode (admin only). until = ISO string (on until then) or
  // null to end now. While active the agent engine is disabled and the on-host
  // control panel is locked, so an admin runs OBS/streaming by hand.
  setBroadcastMode: (facilityId: string, until: string | null): Promise<{ facilityId: string; broadcastModeUntil: string | null }> =>
    httpClient.put(`/api/admin/facilities/${facilityId}/broadcast-mode`, { until }),
};

export const facilitiesApi = {
  // Get active facilities for login (public endpoint)
  getActive: (): Promise<{ facilities: Array<{ facilityId: string; name: string }> }> =>
    httpClient.get('/facilities/active'),

  list: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<FacilitiesListResponse> =>
    httpClient.get('/api/admin/facilities', params),

  getById: (id: number): Promise<Facility> =>
    httpClient.get(`/api/admin/facilities/${id}`),

  create: (data: CreateFacilityRequest): Promise<Facility> =>
    httpClient.post('/api/admin/facilities', data),

  update: (id: number, data: UpdateFacilityRequest): Promise<Facility> =>
    httpClient.patch(`/api/admin/facilities/${id}`, data),

  delete: (id: number): Promise<void> =>
    httpClient.delete(`/api/admin/facilities/${id}`),

  // Facility Account Management
  accounts: {
    list: (facilityId: string): Promise<{
      facilityId: string;
      facilityName: string;
      accounts: Array<{
        id: number;
        email: string;
        createdAt: string;
        updatedAt: string;
        facility: {
          facilityId: string;
          name: string;
        };
      }>;
    }> =>
      httpClient.get(`/api/admin/facilities/${facilityId}/accounts`),

    create: (facilityId: string, data: {
      email: string;
      password: string;
    }): Promise<{
      id: number;
      email: string;
      createdAt: string;
      updatedAt: string;
      facility: {
        facilityId: string;
        name: string;
      };
    }> =>
      httpClient.post(`/api/admin/facilities/${facilityId}/accounts`, data),

    update: (facilityId: string, accountId: number, data: {
      email?: string;
      password?: string;
    }): Promise<{
      id: number;
      email: string;
      createdAt: string;
      updatedAt: string;
      facility: {
        facilityId: string;
        name: string;
      };
    }> =>
      httpClient.patch(`/api/admin/facilities/${facilityId}/accounts/${accountId}`, data),

    delete: (facilityId: string, accountId: number): Promise<void> =>
      httpClient.delete(`/api/admin/facilities/${facilityId}/accounts/${accountId}`),
  },
};

// ── Admin account management (site-independent admin logins) ──────────────────
export interface AdminAccount {
  id: number;
  email: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const adminAccountsApi = {
  list: (): Promise<{ accounts: AdminAccount[] }> =>
    httpClient.get('/api/admin/admin-accounts'),
  create: (data: { email: string; name: string; password: string }): Promise<{ account: AdminAccount }> =>
    httpClient.post('/api/admin/admin-accounts', data),
  update: (id: number, data: { name?: string; isActive?: boolean }): Promise<{ account: AdminAccount }> =>
    httpClient.patch(`/api/admin/admin-accounts/${id}`, data),
  resetPassword: (id: number, password: string): Promise<{ success: boolean }> =>
    httpClient.post(`/api/admin/admin-accounts/${id}/reset-password`, { password }),
  delete: (id: number): Promise<{ success: boolean }> =>
    httpClient.delete(`/api/admin/admin-accounts/${id}`),
};

// Upload API
export const uploadApi = {
  presign: (params: {
    facilityId?: string;
    filename: string;
    contentType: string;
    size: number;
    assetType?: 'slideshow' | 'logo';
  }): Promise<{
    uploadUrl: string;
    fields: Record<string, string>;
    key: string;
    expiresIn: number;
  }> =>
    httpClient.post('/api/uploads/presign', params),

  notify: (data: {
    facilityId?: string;
    uploads: Array<{
      key: string;
      filename: string;
      originalName: string;
      contentType: string;
      size: number;
      width?: number;
      height?: number;
      assetType?: 'slideshow' | 'logo';
    }>;
  }): Promise<{
    success: boolean;
    message: string;
    assets: Array<{
      id: number;
      filename: string;
      originalName: string;
      s3Url: string;
      createdAt: string;
    }>;
  }> =>
    httpClient.post('/api/uploads/notify', data),

  getStatus: (key: string): Promise<{
    key: string;
    exists: boolean;
    asset: {
      id: number;
      filename: string;
      originalName: string;
      s3Url: string;
      isActive: boolean;
      createdAt: string;
    };
  }> =>
    httpClient.get(`/api/uploads/status/${encodeURIComponent(key)}`),
};

// Assets API
export const assetsApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    isActive?: boolean;
    facilityId?: string;
    assetType?: 'slideshow' | 'logo';
  }): Promise<{
    assets: Array<{
      id: number;
      facilityId: string;
      filename: string;
      originalName: string;
      mimeType: string;
      fileSize: number;
      s3Key: string;
      s3Url: string;
      thumbnailUrl?: string;
      assetType: 'slideshow' | 'logo';
      isActive: boolean;
      duration: number;
      sortOrder: number;
      width?: number;
      height?: number;
      uploadedBy?: string;
      createdAt: string;
      updatedAt: string;
      facility: {
        facilityId: string;
        name: string;
      };
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> =>
    httpClient.get('/api/assets', params),

  getById: (id: number): Promise<{
    id: number;
    facilityId: string;
    filename: string;
    originalName: string;
    mimeType: string;
    fileSize: number;
    s3Key: string;
    s3Url: string;
    thumbnailUrl?: string;
    isActive: boolean;
    duration: number;
    sortOrder: number;
    width?: number;
    height?: number;
    uploadedBy?: string;
    createdAt: string;
    updatedAt: string;
    facility: {
      facilityId: string;
      name: string;
    };
  }> =>
    httpClient.get(`/api/assets/${id}`),

  create: (data: {
    filename: string;
    originalName: string;
    mimeType: string;
    fileSize: number;
    s3Key: string;
    s3Url: string;
    thumbnailUrl?: string;
    width?: number;
    height?: number;
    facilityId?: string; // Required for admin users
  }): Promise<{
    id: number;
    facilityId: string;
    filename: string;
    originalName: string;
    s3Url: string;
    isActive: boolean;
    duration: number;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    facility: {
      facilityId: string;
      name: string;
    };
  }> =>
    httpClient.post('/api/assets', data),

  update: (id: number, data: {
    isActive?: boolean;
    duration?: number;
    sortOrder?: number;
  }): Promise<{
    id: number;
    facilityId: string;
    filename: string;
    originalName: string;
    s3Url: string;
    isActive: boolean;
    duration: number;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
    facility: {
      facilityId: string;
      name: string;
    };
  }> =>
    httpClient.patch(`/api/assets/${id}`, data),

  reorder: (data: {
    assetIds: number[];
    facilityId?: string; // Required for admin users
  }): Promise<{
    success: boolean;
    message: string;
  }> =>
    httpClient.post('/api/assets/reorder', data),

  delete: (id: number, deleteS3?: boolean): Promise<void> =>
    httpClient.delete(`/api/assets/${id}?deleteS3=${deleteS3 !== false ? 'true' : 'false'}`),

  getSingularJson: (facilityId: string): Promise<Array<{
    duration: number;
    payload: {
      Sponsor: string;
    };
  }>> =>
    httpClient.get(`/api/assets/singular-json/${facilityId}`),
};

// Migration API
export const migrationApi = {
  checkStatus: (facilityId: string): Promise<{
    facilityId: string;
    facilityName: string;
    isActive: boolean;
    hasExistingAssets: boolean;
    totalAssets: number;
    activeAssets: number;
    canMigrate: boolean;
    requiresOverwriteConfirmation: boolean;
  }> =>
    httpClient.get(`/migration/status/${facilityId}`),

  migrate: (data: {
    facilityId: string;
    singularJson: Array<{
      duration: number;
      payload: {
        Sponsor: string;
      };
    }>;
    overwrite?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
    facilityId: string;
    facilityName: string;
    migrated: Array<{
      id: number;
      originalUrl: string;
      s3Url: string;
      filename: string;
      duration: number;
      sortOrder: number;
    }>;
    failed: Array<{
      url: string;
      reason: string;
    }>;
    summary: {
      total: number;
      succeeded: number;
      failed: number;
    };
  }> =>
    httpClient.post('/migration/migrate', data),
};

// ── Singular JSON → Slideshow Import API ─────────────────────────────────────

export interface SingularSlide {
  duration: number;
  payload: { Sponsor: string };
}

export interface SingularImportResult {
  success: boolean;
  slideshowId: number;
  facilityId: string;
  facilityName: string;
  summary: { total: number; succeeded: number; failed: number };
  images: Array<{ sortOrder: number; s3Url: string; duration: number; originalUrl: string }>;
  failed: Array<{ sortOrder: number; url: string; reason: string }>;
}

export const importApi = {
  importSingular: (data: {
    facilityId: string;
    name: string;
    isDefault?: boolean;
    json: SingularSlide[];
  }): Promise<SingularImportResult> =>
    httpClient.post('/api/admin/import/singular', data),
};

// Health check
export const healthApi = {
  check: (): Promise<{
    status: string;
    timestamp: string;
    uptime: number;
    environment: string;
    database: string;
  }> =>
    httpClient.get('/health'),
};

// ── Slideshow API ─────────────────────────────────────────────────────────────

export interface SlideshowImage {
  id: number;
  slideshowId: number;
  filename: string;
  originalName: string;
  s3Key: string;
  s3Url: string;
  fileSize: number;
  width?: number;
  height?: number;
  /** Seconds on air, one of SLIDE_DURATIONS (8–64 in steps of 8). */
  duration: number;
  sortOrder: number;
  isActive: boolean;
  uploadedBy?: string;
  /** The library image this row shows (null only for rows predating 1.29.0's backfill). */
  libraryImageId?: number | null;
  /** Short-lived URL for previews; set by GET /slideshows/:id. */
  signedUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Slideshow {
  id: number;
  facilityId: string;
  name: string;
  description?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  images?: SlideshowImage[];
  _count?: { images: number };
}

export const slideshowApi = {
  list: (params?: { facilityId?: string }): Promise<{ slideshows: Slideshow[] }> => {
    const qs = params?.facilityId ? `?facilityId=${params.facilityId}` : '';
    return httpClient.get(`/api/slideshows${qs}`);
  },

  // maxFileSize: the facility's per-file upload limit in bytes (null if unknown).
  get: (id: number): Promise<{ slideshow: Slideshow; maxFileSize: number | null }> =>
    httpClient.get(`/api/slideshows/${id}`),

  create: (data: { name: string; description?: string; facilityId?: string }): Promise<{ slideshow: Slideshow }> =>
    httpClient.post('/api/slideshows', data),

  update: (id: number, data: { name?: string; description?: string | null; isActive?: boolean }): Promise<{ slideshow: Slideshow }> =>
    httpClient.patch(`/api/slideshows/${id}`, data),

  setDefault: (id: number): Promise<{ slideshow: Slideshow }> =>
    httpClient.post(`/api/slideshows/${id}/set-default`, {}),

  delete: (id: number): Promise<void> =>
    httpClient.delete(`/api/slideshows/${id}`),

  /** Append library images (in this order) to the slideshow, each at the minimum duration. */
  addImages: (slideshowId: number, libraryImageIds: number[]): Promise<{ images: SlideshowImage[] }> =>
    httpClient.post(`/api/slideshows/${slideshowId}/images`, { libraryImageIds }),

  updateImage: (slideshowId: number, imageId: number, data: { duration?: number; sortOrder?: number; isActive?: boolean }): Promise<{ image: SlideshowImage }> =>
    httpClient.patch(`/api/slideshows/${slideshowId}/images/${imageId}`, data),

  reorderImages: (slideshowId: number, imageIds: number[]): Promise<{ success: boolean }> =>
    httpClient.post(`/api/slideshows/${slideshowId}/images/reorder`, { imageIds }),

  /** Removes the image from this slideshow only; the file stays in the library. */
  deleteImage: (slideshowId: number, imageId: number): Promise<void> =>
    httpClient.delete(`/api/slideshows/${slideshowId}/images/${imageId}`),
};

/**
 * Allowed slide durations in seconds: 8–64 in steps of 8. Mirrors
 * backend/src/lib/slideDuration.ts — change both together.
 */
export const SLIDE_DURATIONS = [8, 16, 24, 32, 40, 48, 56, 64] as const;

// ── Slideshow image library (1.29.0) ──────────────────────────────────────────

export interface SlideshowLibraryImage {
  id: number;
  facilityId: string;
  filename: string;
  originalName: string;
  contentType: string;
  s3Key: string;
  s3Url: string;
  fileSize: number;
  width?: number | null;
  height?: number | null;
  createdAt: string;
  signedUrl: string;
  /** Active slideshows using this image; non-empty means it can't be deleted. */
  usedIn: { slideshowId: number; name: string }[];
}

export const slideshowLibraryApi = {
  // facilityId: admins only; club managers always get their own facility.
  list: (facilityId?: string): Promise<{ images: SlideshowLibraryImage[]; maxFileSize: number }> =>
    httpClient.get(`/api/slideshow-library${facilityId ? `?facilityId=${encodeURIComponent(facilityId)}` : ''}`),

  presign: (params: { filename: string; contentType: string; size: number; facilityId?: string | undefined }): Promise<{ uploadUrl: string; fields: Record<string, string>; key: string; expiresIn: number }> =>
    httpClient.post('/api/slideshow-library/presign', params),

  notify: (data: { key: string; filename: string; originalName: string; contentType: string; size: number; width?: number; height?: number; facilityId?: string | undefined }): Promise<{ image: SlideshowLibraryImage }> =>
    httpClient.post('/api/slideshow-library/notify', data),

  /** 409 with "Used in X, Y…" while any slideshow still uses the image. */
  remove: (id: number): Promise<void> =>
    httpClient.delete(`/api/slideshow-library/${id}`),
};

// ── Reporting API ──────────────────────────────────────────────────────────────

export interface CpmReportImage {
  id: number;
  originalName: string;
  duration: number;
  sortOrder: number;
}

export interface CpmReportSlideshow {
  id: number;
  name: string;
  isDefault: boolean;
  cycleSeconds: number;
  images: CpmReportImage[];
  rates: {
    per30min: number;
    per1hr: number;
    per2hr: number;
    per3hr: number;
  };
}

export interface CpmReportFacility {
  facilityId: string;
  facilityName: string;
  slideshows: CpmReportSlideshow[];
}

export const reportingApi = {
  getCpm: (params?: { facilityId?: string }): Promise<{ facilities: CpmReportFacility[] }> => {
    const qs = params?.facilityId ? `?facilityId=${params.facilityId}` : '';
    return httpClient.get(`/api/reporting/cpm${qs}`);
  },
};

// Direct S3 upload helper
export async function uploadToS3(
  presignedData: { url: string; fields: Record<string, string> },
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    // Use PUT method with direct file upload (our backend now uses presigned PUT)
    xhr.open('PUT', presignedData.url);
    
    // Set Content-Type from the presigned data
    if (presignedData.fields['Content-Type']) {
      xhr.setRequestHeader('Content-Type', presignedData.fields['Content-Type']);
    }
    
    // Send the file directly
    xhr.send(file);
  });
}

// Template types
export interface Template {
  id: number;
  facilityId: string;
  name: string;
  description?: string;
  isDefault: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  facility: {
    facilityId: string;
    name: string;
  };
  templateAssets: TemplateAsset[];
  _count?: {
    templateAssets: number;
  };
}

export interface TemplateAsset {
  id: number;
  templateId: number;
  assetId: number;
  duration: number;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  asset: {
    id: number;
    filename: string;
    originalName: string;
    s3Url: string;
    thumbnailUrl?: string;
    mimeType: string;
    width?: number;
    height?: number;
  };
}

export interface CreateTemplateRequest {
  name: string;
  description?: string;
  isDefault?: boolean;
  facilityId?: string; // Required for admin users
  assets: Array<{
    assetId: number;
    duration?: number;
    sortOrder: number;
    isActive?: boolean;
  }>;
}

export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  isDefault?: boolean;
}

export interface UpdateTemplateAssetsRequest {
  assets: Array<{
    assetId: number;
    duration?: number;
    sortOrder: number;
    isActive?: boolean;
  }>;
}

// Templates API
export const templatesApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    facilityId?: string;
    isDefault?: boolean;
  }): Promise<{
    templates: Template[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> =>
    httpClient.get('/templates', params),

  getById: (id: number): Promise<Template> =>
    httpClient.get(`/templates/${id}`),

  create: (data: CreateTemplateRequest): Promise<Template> =>
    httpClient.post('/templates', data),

  update: (id: number, data: UpdateTemplateRequest): Promise<Template> =>
    httpClient.patch(`/templates/${id}`, data),

  updateAssets: (id: number, data: UpdateTemplateAssetsRequest): Promise<Template> =>
    httpClient.put(`/templates/${id}/assets`, data),

  apply: (id: number): Promise<{
    success: boolean;
    message: string;
    template: {
      id: number;
      name: string;
      description?: string;
    };
    updatedAssets: Array<{
      id: number;
      facilityId: string;
      filename: string;
      originalName: string;
      s3Url: string;
      isActive: boolean;
      duration: number;
      sortOrder: number;
      facility: {
        facilityId: string;
        name: string;
      };
    }>;
  }> =>
    httpClient.post(`/templates/${id}/apply`),

  getAssetsUsedElsewhere: (id: number): Promise<{
    sharedAssets: Array<{
      assetId: number;
      originalName: string;
      filename: string;
      templatesUsingThis: Array<{
        id: number;
        name: string;
        description?: string;
      }>;
    }>;
    exclusiveAssets: Array<{
      assetId: number;
      originalName: string;
      filename: string;
    }>;
  }> =>
    httpClient.get(`/templates/${id}/assets-used-elsewhere`),

  delete: (id: number): Promise<void> =>
    httpClient.delete(`/templates/${id}`),
};

// ── Overlay API ───────────────────────────────────────────────────────────────

export interface OverlayWidget {
  id: number;
  sceneId: number;
  type: 'text' | 'image' | 'slideshow' | 'clock' | 'ticker' | 'shape' | 'scoreboard';
  name: string;
  x: number; y: number; width: number; height: number;
  rotation: number; opacity: number; zIndex: number;
  isVisible: boolean; isLocked: boolean;
  properties: Record<string, unknown>;
  assetIds: number[];
  createdAt: string; updatedAt: string;
}

export interface OverlayScene {
  id: number;
  compositionId: number;
  name: string;
  sortOrder: number;
  isDefault: boolean;
  widgets: OverlayWidget[];
  createdAt: string; updatedAt: string;
}

export interface OverlayComposition {
  id: number;
  facilityId: string;
  sheetId: string;
  name: string;
  description?: string;
  eventName?: string | null;
  isActive: boolean;
  isPublished: boolean;
  isTemplate: boolean;
  isEventOverride: boolean;
  isOverrideActive: boolean;
  activeSceneId: number | null;
  activeColorSchemeId: number | null;
  activeColorScheme?: ColorScheme | null;
  scenes: OverlayScene[];
  createdAt: string; updatedAt: string;
}

// ── Color Scheme types ────────────────────────────────────────────────────────
export type RoleSolidValue = { type: 'solid'; color: string };
export type RoleGradientValue = {
  type: 'gradient';
  mode: 'linear' | 'radial';
  angle: number;
  stops: Array<{ color: string; position: number }>;
};
export type RoleValue = RoleSolidValue | RoleGradientValue;
export type Palette = Record<string, RoleValue>;

export interface RoleDefinition {
  id: string;
  label: string;
  description: string;
  gradientCapable: boolean;
  default: RoleValue;
}

export interface ColorScheme {
  id: number;
  facilityId: string | null;
  name: string;
  description?: string | null;
  palette: Palette;
  isGlobal: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

/** What an overlay's widgets are currently set to, shaped as a scheme palette. */
export interface ColorSnapshot {
  overlayName: string;
  facilityId: string;
  palette: Palette;
  /** One entry per role some widget contributed a colour to. */
  captured: {
    roleId: string;
    value: RoleValue;
    /** Widgets that contributed — more than one means they share the role. */
    widgets: string[];
    /** Several widgets feed this role and are set differently, so one won. */
    shared: boolean;
    /** The widget whose value was used. */
    chosenFrom: string;
  }[];
  widgetsWithBindings: number;
  widgetsWithoutBindings: number;
}

export interface OverlayCopyTarget {
  id: number;
  name: string;
  sheetId: string | null;
  facilityId: string;
  isEventOverride: boolean;
  isPublished: boolean;
  scenes: { id: number; name: string; isDefault: boolean }[];
}

export const overlaysApi = {
  listCompositions: (params?: { facilityId?: string; sheetId?: string }) =>
    httpClient.get<{ compositions: OverlayComposition[] }>('/api/overlays/compositions', params),
  getComposition: (id: number) =>
    httpClient.get<{ composition: OverlayComposition }>(`/api/overlays/compositions/${id}`),
  createComposition: (data: { facilityId: string; sheetId: string; name: string; description?: string }) =>
    httpClient.post<{ composition: OverlayComposition }>('/api/overlays/compositions', data),
  updateComposition: (id: number, data: Partial<OverlayComposition>) =>
    httpClient.put<{ composition: OverlayComposition }>(`/api/overlays/compositions/${id}`, data),
  deleteComposition: (id: number) =>
    httpClient.delete<void>(`/api/overlays/compositions/${id}`),
  publishComposition: (id: number) =>
    httpClient.post<{ composition: OverlayComposition }>(`/api/overlays/compositions/${id}/publish`),
  unpublishComposition: (id: number) =>
    httpClient.post<{ composition: OverlayComposition }>(`/api/overlays/compositions/${id}/unpublish`),
  duplicateComposition: (id: number, data: { facilityId: string; sheetId: string; name: string; description?: string }) =>
    httpClient.post<{ composition: OverlayComposition }>(`/api/overlays/compositions/${id}/duplicate`, data),
  createFromTemplate: (templateId: number, data: { facilityId: string; sheetId: string; name: string; description?: string }) =>
    httpClient.post<{ composition: OverlayComposition }>(`/api/overlays/compositions/from-template/${templateId}`, data),
  createScene: (compositionId: number, data: { name: string; sortOrder?: number; isDefault?: boolean }) =>
    httpClient.post<{ scene: OverlayScene }>(`/api/overlays/compositions/${compositionId}/scenes`, data),
  updateScene: (sceneId: number, data: Partial<OverlayScene>) =>
    httpClient.put<{ scene: OverlayScene }>(`/api/overlays/scenes/${sceneId}`, data),
  deleteScene: (sceneId: number) =>
    httpClient.delete<void>(`/api/overlays/scenes/${sceneId}`),
  activateScene: (sceneId: number) =>
    httpClient.post<{ ok: boolean; activeSceneId: number }>(`/api/overlays/scenes/${sceneId}/activate`),
  createWidget: (sceneId: number, data: Omit<OverlayWidget, 'id' | 'sceneId' | 'createdAt' | 'updatedAt'>) =>
    httpClient.post<{ widget: OverlayWidget }>(`/api/overlays/scenes/${sceneId}/widgets`, data),
  /**
   * The palette a new scheme would start from if it took its values from this
   * overlay's widgets. Read-only — nothing is saved until the scheme is created.
   */
  getColorSnapshot: (compositionId: number) =>
    httpClient.get<ColorSnapshot>(`/api/overlays/compositions/${compositionId}/color-snapshot`),
  /** Overlays a widget can be copied into, fleet-wide. Admin only. */
  listCopyTargets: () =>
    httpClient.get<{ targets: OverlayCopyTarget[]; facilityNames: Record<string, string> }>(
      '/api/overlays/copy-targets'),
  /**
   * Copy a configured widget into a scene of another overlay, in this or any
   * other facility. Across facilities the server re-points asset and slideshow
   * references to the target's own media and reports that in `adjustments`.
   */
  copyWidgetToScene: (widgetId: number, targetSceneId: number) =>
    httpClient.post<{
      widget: OverlayWidget;
      targetCompositionId: number;
      targetOverlayName: string;
      targetFacilityId: string;
      crossFacility: boolean;
      adjustments: string[];
    }>(`/api/overlays/widgets/${widgetId}/copy`, { targetSceneId }),
  updateWidget: (widgetId: number, data: Partial<OverlayWidget>) =>
    httpClient.put<{ widget: OverlayWidget }>(`/api/overlays/widgets/${widgetId}`, data),
  deleteWidget: (widgetId: number) =>
    httpClient.delete<void>(`/api/overlays/widgets/${widgetId}`),
  batchUpdateWidgets: (sceneId: number, widgets: Array<{ id: number } & Partial<OverlayWidget>>) =>
    httpClient.post<{ ok: boolean }>(`/api/overlays/scenes/${sceneId}/widgets/batch-update`, { widgets }),
  assignSlideshow: (widgetId: number, slideshowId: number | null) =>
    httpClient.patch<{ widget: OverlayWidget }>(`/api/overlays/widgets/${widgetId}/assign-slideshow`, { slideshowId }),
  setActiveScheme: (compositionId: number, schemeId: number | null) =>
    httpClient.put<{ composition: OverlayComposition }>(
      `/api/overlays/compositions/${compositionId}/active-scheme`,
      { schemeId },
    ),
  cloneAsEventOverride: (sourceCompositionId: number, data: { name?: string; description?: string }) =>
    httpClient.post<{ composition: OverlayComposition }>(
      `/api/overlays/compositions/${sourceCompositionId}/clone-as-event-override`,
      data,
    ),
};

// ── Color Schemes API ─────────────────────────────────────────────────────────
export const colorSchemesApi = {
  listRoles: () =>
    httpClient.get<{ roles: RoleDefinition[] }>('/api/color-schemes/roles'),
  list: (params?: { facilityId?: string }) =>
    httpClient.get<{ schemes: ColorScheme[] }>('/api/color-schemes', params),
  get: (id: number) =>
    httpClient.get<{ scheme: ColorScheme }>(`/api/color-schemes/${id}`),
  create: (data: {
    facilityId?: string;
    name: string;
    description?: string;
    palette?: Palette;
    isGlobal?: boolean;
    isDefault?: boolean;
  }) =>
    httpClient.post<{ scheme: ColorScheme }>('/api/color-schemes', data),
  update: (id: number, data: {
    name?: string;
    description?: string;
    palette?: Palette;
    isDefault?: boolean;
  }) =>
    httpClient.put<{ scheme: ColorScheme }>(`/api/color-schemes/${id}`, data),
  delete: (id: number) =>
    httpClient.delete<void>(`/api/color-schemes/${id}`),
  promoteGlobal: (id: number) =>
    httpClient.post<{ scheme: ColorScheme }>(`/api/color-schemes/${id}/promote-global`),
};

// ── Schedule API ──────────────────────────────────────────────────────────────

export type ScheduleHealthGroup = 'sync_problem' | 'has_games' | 'connected_empty' | 'manual_empty' | 'not_set_up';

export interface ScheduleHealthSite {
  facilityId: string;
  name: string;
  timezone: string;
  scheduleSource: string;
  automatic: boolean;
  configured: boolean;
  /** Why the sync looks wrong, when it does. */
  problem: string | null;
  group: ScheduleHealthGroup;
  lastSync: string | null;
  issueCount: number;
  hostCount: number;
  gamesNext7Days: number;
  gamesToday: number;
  nextGame: { at: string; eventName: string; sheetId: string } | null;
  lastGame: { at: string; status: string } | null;
}

export interface ScheduleHealth {
  generatedAt: string;
  windowDays: number;
  counts: { total: number; hasGames: number; syncProblem: number; connectedEmpty: number; manualEmpty: number; notSetUp: number };
  sites: ScheduleHealthSite[];
}

export interface AlertChannelStatus { configured: boolean; preview: string | null; updatedAt: string | null }

/** One sheet's OBS camera setup as recorded by the agent (camera inventory). */
export interface CameraSetup {
  id: number;
  sheetName: string;
  obsPort: number | null;
  obsExePath: string | null;
  sceneCollection: string | null;
  ndiOutputName: string | null;
  inputs: Array<{ inputName: string; inputKind: string; settings: Record<string, unknown>; receiving: boolean }>;
  allReceiving: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
  lastReceivingAt: string | null;
}

export interface CameraInventorySheet {
  sheetName: string;
  /** The setup seen most recently. */
  current: CameraSetup;
  /** Latest setup seen with every camera input receiving video. */
  lastGood: CameraSetup | null;
  currentIsLastGood: boolean;
  history: Array<{ id: number; firstSeenAt: string; lastSeenAt: string; lastReceivingAt: string | null; allReceiving: boolean; sources: string[] }>;
}

/** 409 body when an ad-hoc start would block scheduled games on that sheet. */
export interface AdhocConflictResponse {
  code: 'ADHOC_SCHEDULE_CONFLICT';
  message: string;
  conflicts: Array<{ id: number; eventName: string; team1Name: string | null; team2Name: string | null;
    scheduledStart: string; scheduledEnd: string }>;
  until: string;
  untilEndOfDay: boolean;
  timezone: string;
}

/**
 * "When the title contains matchValue, the game lasts durationMinutes" — or,
 * with action "skip", "don't stream it" (durationMinutes unused, sent as 0).
 */
export interface ScheduleRuleInput {
  id?: number;
  matchValue: string;
  action: 'duration' | 'skip';
  /** What matchValue is compared with: title contains, either team contains, or exact sheet(s). */
  matchField: 'eventName' | 'teams' | 'sheet';
  durationMinutes: number;
  /** Beat the source's own length, not just fill in where it sends none. */
  overridesSource: boolean;
  isActive: boolean;
}

export interface ScheduleRule extends ScheduleRuleInput {
  id: number;
  facilityId: string;
  sortOrder: number;
}

export interface ScheduleRulesSummary {
  upcomingGames: number;
  onDefault: number;
  onRule: number;
  onSource: number;
  notYetSynced: number;
  /** Upcoming games a Skip rule keeps off air. */
  skipped?: number;
}

export interface ScheduleRulesResponse {
  rules: ScheduleRule[];
  /** The site's default game length; null = not set, CSM uses fallbackMinutes. */
  defaultMinutes: number | null;
  fallbackMinutes: number;
  ruleCapable: boolean;
  /** Google Sheets: every row has its own end, so only Override rules act and the default never applies. */
  overrideOnly?: boolean;
  scheduleSource: string;
  summary: ScheduleRulesSummary;
}

export interface ScheduleRulesImpact extends ScheduleRulesSummary {
  changedCount: number;
  changed: Array<{ id: number; eventName: string; sheetId: string; scheduledStart: string;
    currentMinutes: number; newMinutes: number;
    /** "length" = its length changes; "skip" = newly skipped; "unskip" = no longer skipped. */
    change: 'length' | 'skip' | 'unskip' }>;
}

export interface StreamSchedule {
  id: number;
  facilityId: string;
  sheetId: string;
  eventName: string;
  team1Name?: string;
  team2Name?: string;
  scheduledStart: string;
  scheduledEnd: string;
  status: 'pending' | 'streaming' | 'completed' | 'cancelled' | 'skipped';
  isPaused: boolean;
  /** Set by a sync when the source's data for this game needed interpreting. */
  syncWarning?: string | null;
  /** Where the end came from at the last sync: the feed, a Schedule Rule, or CSM's 2-hour default. */
  endSource?: 'source' | 'rule' | 'default' | null;
  /** The Skip rule (its text) that keeps this game off air; paused unless the club resumed it. */
  skipRule?: string | null;
  isActive: boolean;
  streamStartedAt?: string;
  streamEndedAt?: string;
  overlayCompositionId?: number;
  notes?: string;
  obsSceneName?: string;
  eventId?: number | null;
  drawLabel?: string | null;
  /** CurlOps game id for matches of a CurlOps-linked event. */
  sourceRowId?: string | null;
  /** YouTube broadcast of this game's current/last run (watch link). */
  youtubeBroadcastId?: string | null;
  manuallyEdited?: boolean;
  startupLog?: string | null;
  createdAt: string; updatedAt: string;
}

export interface BlackoutDate {
  id: number;
  facilityId?: string;
  startDate: string;
  endDate: string;
  reason?: string;
  isActive: boolean;
  createdAt: string; updatedAt: string;
}

/** One site’s worth of rows in the fleet stream views. */
export interface FleetScheduleRow {
  id: number;
  sheetId: string;
  eventName: string | null;
  team1Name: string | null;
  team2Name: string | null;
  scheduledStart: string;
  scheduledEnd: string;
  status: string;
  isPaused: boolean;
  isEventMatch: boolean;
  /** Why the last sync flagged this game (tight/overlapping draw…), if it did. */
  syncWarning?: string | null;
}

/** One entry of a site's last-sync issue list (Facility.lastSyncIssues). */
export interface FleetSyncIssue {
  /** 'prep' = CSM's own sheet preparation failed (transform/padding). */
  kind?: string;
  row?: number | null;
  sheet?: string | null;
  event?: string | null;
  start?: string | null;
  reason?: string;
}

export interface FleetLiveGroup {
  facilityId: string;
  facilityName: string;
  timezone: string;
  agentOnline: boolean;
  /** True when the list came from schedule rows because no agent answered. */
  fromDatabase: boolean;
  entries: Array<{
    sheetId: string;
    host: string | null;
    broadcastId: string | null;
    unscheduled: boolean;
    schedule: FleetScheduleRow | null;
  }>;
}

export interface FleetUpcomingGroup {
  facilityId: string;
  facilityName: string;
  timezone: string;
  agentOnline: boolean;
  localDayEnds: string;
  rows: FleetScheduleRow[];
  /** Everything the site's last sync could not read or flagged. May be the only reason the site is listed. */
  issues: FleetSyncIssue[];
}

export interface FleetOverview {
  generatedAt: string;
  live: FleetLiveGroup[];
  upcoming: FleetUpcomingGroup[];
  counts: { live: number; liveSites: number; upcomingToday: number; upcomingSites: number; issueSites?: number };
}

export const schedulesApi = {
  list: (params?: { facilityId?: string; sheetId?: string; from?: string; to?: string; status?: string }) =>
    httpClient.get<{ schedules: StreamSchedule[] }>('/api/schedules', params),
  get: (id: number) =>
    httpClient.get<{ schedule: StreamSchedule }>(`/api/schedules/${id}`),
  create: (data: Partial<StreamSchedule>) =>
    httpClient.post<{ schedule: StreamSchedule }>('/api/schedules', data),
  update: (id: number, data: Partial<StreamSchedule>) =>
    httpClient.put<{ schedule: StreamSchedule }>(`/api/schedules/${id}`, data),
  delete: (id: number) =>
    httpClient.delete<void>(`/api/schedules/${id}`),
  pause: (id: number) =>
    httpClient.post<{ schedule: StreamSchedule }>(`/api/schedules/${id}/pause`),
  resume: (id: number) =>
    httpClient.post<{ schedule: StreamSchedule }>(`/api/schedules/${id}/resume`),
  cancel: (id: number) =>
    httpClient.post<{ schedule: StreamSchedule }>(`/api/schedules/${id}/cancel`),
  importCsv: (facilityId: string, csvData: string, confirm = false) =>
    httpClient.post<{ preview?: unknown[]; imported?: number; schedules?: StreamSchedule[]; timezone?: string }>('/api/schedules/import-csv', { facilityId, csvData, confirm }),
  syncFromSheets: (facilityId: string, confirm = false) =>
    httpClient.post<{ preview?: unknown[]; imported?: number; schedules?: StreamSchedule[]; timezone?: string }>('/api/schedules/sync-from-sheets', { facilityId, confirm }),
  syncFromCcm: (facilityId: string, confirm = false) =>
    httpClient.post<{ preview?: unknown[]; imported?: number; skippedBye?: number; timezone?: string }>('/api/schedules/sync-from-ccm', { facilityId, confirm }),
  syncFromLeagueApps: (facilityId: string, confirm = false) =>
    httpClient.post<{ preview?: unknown[]; imported?: number; skippedBye?: number; gamesFetched?: number; note?: string; timezone?: string }>('/api/schedules/sync-from-leagueapps', { facilityId, confirm }),
  syncFromGeneric: (facilityId: string) =>
    httpClient.post<{ imported?: number; deleted?: number; entryCount?: number; invalidRows?: number; syncedAt?: string }>('/api/schedules/sync-from-generic', { facilityId }),
  syncFromCurlOps: (facilityId: string, confirm = false) =>
    httpClient.post<{ preview?: unknown[]; imported?: number; deleted?: number; skippedBye?: number; gamesFetched?: number; timezone?: string }>('/api/schedules/sync-from-curlops', { facilityId, confirm }),

  // Generic recurring schedule entry CRUD
  listGenericEntries: (facilityId: string) =>
    httpClient.get<{ entries: GenericScheduleEntry[] }>('/api/schedules/generic-entries', { facilityId }),
  createGenericEntry: (facilityId: string, data: {
    dayOfWeek: number; startTime: string; endTime: string;
    leagueName?: string | null; sheetIds?: string[]; isActive?: boolean;
  }) =>
    httpClient.post<{ entry: GenericScheduleEntry }>('/api/schedules/generic-entries', { facilityId, ...data }),
  updateGenericEntry: (id: number, data: Partial<{
    dayOfWeek: number; startTime: string; endTime: string;
    leagueName: string | null; sheetIds: string[]; isActive: boolean;
  }>) =>
    httpClient.put<{ entry: GenericScheduleEntry }>(`/api/schedules/generic-entries/${id}`, data),
  deleteGenericEntry: (id: number) =>
    httpClient.delete<void>(`/api/schedules/generic-entries/${id}`),

  // Wipe all future events (clean slate for re-import)
  wipeFuture: (facilityId: string) =>
    httpClient.post<{ deleted: number }>('/api/schedules/wipe-future', { facilityId }),

  // Facility-wide streaming pause
  pauseAll: (facilityId: string, paused: boolean) =>
    httpClient.post<{ facilityId: string; streamingPaused: boolean }>('/api/schedules/pause-all', { facilityId, paused }),

  // Remote command queue (start/stop from the website)
  sendAgentCommand: (data: {
    facilityId?: string; type: 'start' | 'start_adhoc' | 'stop';
    sheetName?: string; scheduleId?: number;
    eventName?: string; team1Name?: string; team2Name?: string; notes?: string; obsSceneName?: string;
    privacyStatus?: 'public' | 'unlisted' | 'private';
    /** Ad-hoc only: auto-stop this many minutes after going live. */
    durationMinutes?: number;
    /**
     * Ad-hoc only: what to do about scheduled games the stream would block.
     * Omitted and there are some → 409 with code ADHOC_SCHEDULE_CONFLICT and
     * the games (see AdhocConflictResponse).
     */
    conflictAction?: 'pause' | 'ignore';
    /** CurlOps-linked event matches: the event's override code (club) or override (admin). */
    overrideCode?: string;
    override?: boolean;
  }) => httpClient.post<{ commandId: number; pausedGames?: number }>('/api/schedules/agent-command', data),
  startDraw: (data: { facilityId?: string; scheduledStart: string; eventId?: number; scheduleIds?: number[]; overrideCode?: string; override?: boolean }) =>
    httpClient.post<{ started: number; results: Array<{ sheetName: string; commandId?: number; error?: string }> }>('/api/schedules/start-draw', data),
  getAgentCommands: (facilityId: string) =>
    httpClient.get<{ commands: AgentCommandStatus[] }>('/api/schedules/agent-commands', { facilityId }),
  listBlackouts: (params?: { facilityId?: string }) =>
    httpClient.get<{ blackouts: BlackoutDate[] }>('/api/schedules/blackouts', params),
  createBlackout: (data: { facilityId?: string; startDate: string; endDate: string; reason?: string }) =>
    httpClient.post<{ blackout: BlackoutDate }>('/api/schedules/blackouts', data),
  updateBlackout: (id: number, data: Partial<BlackoutDate>) =>
    httpClient.put<{ blackout: BlackoutDate }>(`/api/schedules/blackouts/${id}`, data),
  deleteBlackout: (id: number) =>
    httpClient.delete<void>(`/api/schedules/blackouts/${id}`),
  downloadAgentConfig: (facilityId: string) =>
    httpClient.get<unknown>(`/api/schedules/agent-config/${facilityId}/download`),
  // ── Schedule Rules (per-facility game lengths) ──
  listScheduleRules: (facilityId?: string) =>
    httpClient.get<ScheduleRulesResponse>('/api/schedule-rules', facilityId ? { facilityId } : undefined),
  previewScheduleRules: (data: { facilityId?: string; rules: ScheduleRuleInput[]; defaultMinutes?: number | null }) =>
    httpClient.post<ScheduleRulesImpact>('/api/schedule-rules/preview', data),
  createScheduleRule: (data: ScheduleRuleInput & { facilityId?: string }) =>
    httpClient.post<{ rule: ScheduleRule }>('/api/schedule-rules', data),
  updateScheduleRule: (id: number, data: Partial<ScheduleRuleInput>) =>
    httpClient.put<{ rule: ScheduleRule }>(`/api/schedule-rules/${id}`, data),
  deleteScheduleRule: (id: number) =>
    httpClient.delete<void>(`/api/schedule-rules/${id}`),
  setDefaultGameLength: (data: { facilityId?: string; defaultMinutes: number | null }) =>
    httpClient.put<{ defaultMinutes: number | null }>('/api/schedule-rules/default', data),
  reorderScheduleRules: (data: { facilityId?: string; ids: number[] }) =>
    httpClient.post<{ rules: ScheduleRule[] }>('/api/schedule-rules/reorder', data),
  /**
   * What is on air now and what is still to come today, grouped by site.
   * Live is reconciled against agent heartbeats, so it matches the dashboard
   * tile and includes ad-hoc streams that have no schedule row. Admin only.
   */
  getFleetOverview: () =>
    httpClient.get<FleetOverview>('/api/schedules/fleet-overview'),
  /** Which sites have league games in the next 7 days, and which don't. Admin only. */
  getScheduleHealth: () =>
    httpClient.get<ScheduleHealth>('/api/schedules/schedule-health'),
  getAgentStatus: (facilityId: string) =>
    httpClient.get<{
      hosts: Array<{
        host: AgentHost;
        isOnline: boolean;
        lastHeartbeat: { seenAt: string } | null;
        hostState: string | null;
        sessions: Array<{
          sheetName: string;
          state: string;
          obsConnected: boolean;
          broadcastId: string | null;
          broadcastStatus: string | null;
          lastError: string | null;
          isUnscheduled: boolean;
          startedRemotelyBy?: string | null;
          curlOpsControlled?: boolean;
        }>;
        /** NDI Discovery Server monitor (agent 1.0.37+); null on older agents. */
        ndiDiscovery?: {
          expected: boolean;
          state: 'ok' | 'restarting' | 'faulted' | 'not_expected' | 'unknown';
          running: boolean;
          restartsToday: number;
          lastStoppedAt: string | null;
          lastRestartAt: string | null;
          lastError: string | null;
        } | null;
      }>;
      recentLogs: unknown[];
    }>(`/api/agent/status/${facilityId}`),
};

// ── Remote command status (staged-progress UI) ────────────────────────────────
export interface AgentCommandStatus {
  id: number;
  sheetName: string;
  type: 'start' | 'start_adhoc' | 'stop';
  status: 'pending' | 'delivered' | 'running' | 'done' | 'failed';
  stage: string | null;
  message: string | null;
  createdAt: string;
  completedAt: string | null;
  scheduleId: number | null;
  stageHistory: Array<{ stage: string; at: string }>;
}

// ── Event Mode ────────────────────────────────────────────────────────────────
export type EventFormat = 'four_person' | 'mixed_doubles';

export interface EventSummary {
  id: number;
  facilityId: string;
  name: string;
  // 'draft' = prepared in advance, league NOT paused; 'active' = in Event Mode.
  status: 'draft' | 'active' | 'ended';
  eventModeUntil: string | null;
  format: EventFormat | null;
  numEnds: number | null;
  googleSheetUrl: string | null;
  thumbnailS3Key: string | null;
  thumbnailMime: string | null;
  /** Set = games sync from this CurlOps event and are started/stopped from CurlOps. */
  curlOpsEventId?: number | null;
  hasOverrideCode?: boolean;
  lastCurlOpsSync?: string | null;
  matchCount?: number;
  isInEventMode?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const eventsApi = {
  list: (facilityId: string) =>
    httpClient.get<{ events: EventSummary[] }>('/api/events', { facilityId }),
  get: (id: number) =>
    httpClient.get<{ event: EventSummary & { matches: StreamSchedule[] } }>(`/api/events/${id}`),
  create: (data: { facilityId?: string; name: string; status?: 'draft' | 'active'; eventModeUntil?: string; format?: EventFormat | null; numEnds?: number | null; googleSheetUrl?: string | null; thumbnailS3Key?: string | null; thumbnailMime?: string | null; curlOpsEventId?: number | null; overrideCode?: string | null }) =>
    httpClient.post<{ event: EventSummary; sync?: CurlOpsEventSync; syncError?: string }>('/api/events', data),
  update: (id: number, data: Partial<{ name: string; eventModeUntil: string; format: EventFormat | null; numEnds: number | null; googleSheetUrl: string | null; thumbnailS3Key: string | null; thumbnailMime: string | null; curlOpsEventId: number | null; overrideCode: string | null }>) =>
    httpClient.put<{ event: EventSummary; sync?: CurlOpsEventSync; syncError?: string }>(`/api/events/${id}`, data),
  // Flip a prepared draft into Event Mode now (sets end time, pauses league).
  activate: (id: number, eventModeUntil: string) =>
    httpClient.post<{ event: EventSummary }>(`/api/events/${id}/activate`, { eventModeUntil }),
  end: (id: number) =>
    httpClient.post<{ event: EventSummary }>(`/api/events/${id}/end`),
  delete: (id: number) =>
    httpClient.delete<void>(`/api/events/${id}`),
  thumbnailPresign: (data: { facilityId?: string; contentType: string; ext: string; size: number }) =>
    httpClient.post<{ uploadUrl: string; fields: Record<string, string>; key: string; contentType: string }>('/api/events/thumbnail-presign', data),
  /** Preview (confirm=false) returns the plan; confirm applies the plan with that hash (409 + new plan if the sheet changed). */
  importMatches: (id: number, body: { confirm: boolean; planHash?: string; acks?: { underway?: boolean; remove?: boolean; dragFill?: boolean } }) =>
    httpClient.post<{ plan?: EventImportPlan; timezone?: string; totalCount?: number; added?: number; changed?: number; removed?: number; unchanged?: number; kept?: number; problems?: number }>(`/api/events/${id}/import`, body),
  sheetsConfig: () =>
    httpClient.get<{ enabled: boolean }>('/api/events/sheets-config'),
  createSheet: (data: { facilityId?: string; eventId?: number; name?: string }) =>
    httpClient.post<{ url: string; spreadsheetId: string }>('/api/events/create-sheet', data),
  /** Pull a CurlOps-linked event's games from CurlOps now. */
  curlOpsSync: (id: number) =>
    httpClient.post<{ sync: CurlOpsEventSync }>(`/api/events/${id}/curlops-sync`),
};

export interface CurlOpsEventSync {
  eventId: number; curlOpsEventId: number;
  created: number; updated: number; removed: number; unchanged: number;
  problems: string[]; syncedAt: string;
}

/** 423 body when a CurlOps-linked match is started/stopped from CSM without the override. */
export const CURLOPS_LOCK_CODES = ['CURLOPS_LOCKED', 'CURLOPS_BAD_CODE'] as const;

// ── YouTube API ───────────────────────────────────────────────────────────────

export const youtubeApi = {
  getCredentials: (facilityId: string) =>
    httpClient.get<{ connected: boolean; channelId?: string; channelName?: string; tokenExpiresAt?: string; isActive?: boolean }>(`/api/youtube/credentials/${facilityId}`),
  deleteCredentials: (facilityId: string) =>
    httpClient.delete<void>(`/api/youtube/credentials/${facilityId}`),
  getRenameRule: (facilityId: string) =>
    httpClient.get<{ rule: { titleTemplate: string; isActive: boolean } | null }>(`/api/youtube/rename-rules/${facilityId}`),
  saveRenameRule: (facilityId: string, titleTemplate: string) =>
    httpClient.post<{ rule: unknown }>('/api/youtube/rename-rules', { facilityId, titleTemplate }),
  updateRenameRule: (facilityId: string, data: { titleTemplate?: string; isActive?: boolean }) =>
    httpClient.put<{ rule: unknown }>(`/api/youtube/rename-rules/${facilityId}`, data),
  getRenameLogs: (params?: { facilityId?: string; status?: string; from?: string; to?: string }) =>
    httpClient.get<{ logs: Array<{ id: number; facilityId: string; status: string; originalTitle?: string; newTitle?: string; errorMessage?: string; createdAt: string; schedule?: { eventName: string; team1Name?: string; team2Name?: string; sheetId: string } }> }>('/api/youtube/rename-logs', params),
  retryRename: (id: number) =>
    httpClient.post<{ ok: boolean }>(`/api/youtube/rename-logs/${id}/retry`),
  // Stream-key registry (Create Keys button)
  getStreamKeyStatus: (facilityId: string) =>
    httpClient.get<{
      facilityId: string;
      connected: boolean;
      total: number;
      withKey: number;
      missing: number;
      sheets: Array<{ sheetName: string; streamKeyName: string; status: string; hasKey: boolean }>;
    }>(`/api/youtube/stream-keys/${facilityId}`),
  createStreamKeys: (facilityId: string) =>
    httpClient.post<{
      facilityId: string;
      found: number;
      created: number;
      missing: number;
      sheets: Array<{ sheetName: string; streamKeyName: string; status: string }>;
    }>(`/api/youtube/stream-keys/${facilityId}/create`),
};

// ── Live broadcast title/description (read + edit on demand) ──────────────────
export interface LiveBroadcast {
  sheetName: string;
  broadcastId: string;
  state: string | null;
  title: string | null;
  description: string | null;
  youtubeStatus: string | null;
  watchUrl: string;
  found: boolean;
  isUnscheduled?: boolean;
  eventName?: string | null;
}

export const liveBroadcastsApi = {
  list: (facilityId: string) =>
    httpClient.get<{ facilityId: string; broadcasts: LiveBroadcast[] }>(`/api/youtube/live-broadcasts/${facilityId}`),
  update: (facilityId: string, data: { sheetName: string; title: string; description: string }) =>
    httpClient.put<{ facilityId: string; sheetName: string; broadcastId: string; title: string; description: string }>(`/api/youtube/live-broadcasts/${facilityId}`, data),
  applyTemplate: (facilityId: string, data: { sheetName: string; templateId: number }) =>
    httpClient.post<{ facilityId: string; sheetName: string; broadcastId: string; title: string; description: string; thumbnailApplied: boolean; thumbnailError?: string }>(`/api/youtube/live-broadcasts/${facilityId}/apply-template`, data),
};

// ── Metadata templates (saved title/description/thumbnail) ────────────────────
export interface MetadataTemplate {
  id: number;
  name: string;
  titleTemplate: string;
  descriptionTemplate: string;
  isDefault: boolean;
  hasThumbnail: boolean;
  thumbnailUrl: string | null;
}

export interface MetadataTemplateInput {
  name: string;
  titleTemplate: string;
  descriptionTemplate: string;
  thumbnailS3Key?: string | null;
  thumbnailMime?: string | null;
  isDefault?: boolean;
}

export const metadataTemplatesApi = {
  list: (facilityId: string) =>
    httpClient.get<{ facilityId: string; templates: MetadataTemplate[] }>(`/api/youtube/metadata-templates/${facilityId}`),
  create: (facilityId: string, data: MetadataTemplateInput) =>
    httpClient.post<{ template: MetadataTemplate }>(`/api/youtube/metadata-templates/${facilityId}`, data),
  update: (facilityId: string, id: number, data: MetadataTemplateInput) =>
    httpClient.put<{ template: MetadataTemplate }>(`/api/youtube/metadata-templates/${facilityId}/${id}`, data),
  delete: (facilityId: string, id: number) =>
    httpClient.delete<{ success: boolean }>(`/api/youtube/metadata-templates/${facilityId}/${id}`),
  thumbnailPresign: (facilityId: string, data: { filename: string; contentType: string; size: number }) =>
    httpClient.post<{ uploadUrl: string; fields: Record<string, string>; key: string; contentType: string }>(`/api/youtube/metadata-templates/${facilityId}/thumbnail-presign`, data),
};

// ── OBS profile template (standard streaming profile) ─────────────────────────
export interface ObsTemplateSummary {
  resolution: string | null;
  fps: number | null;
  encoder: string | null;
  rateControl: string | null;
  bitrateKbps: number | null;
}
export const obsTemplateApi = {
  get: () =>
    httpClient.get<{
      template: { profileName: string; version: number; basicIni: string; serviceJson: string; streamEncoderJson: string; updatedAt: string };
      summary: ObsTemplateSummary;
    }>('/api/obs-template'),
  update: (data: { basicIni: string; serviceJson: string; streamEncoderJson: string; profileName?: string }) =>
    httpClient.put<{ template: { profileName: string; version: number; updatedAt: string }; summary: ObsTemplateSummary }>('/api/obs-template', data),
  apply: (facilityId: string, sheetNames?: string[]) =>
    httpClient.post<{ facilityId: string; version: number; targeted: number }>('/api/obs-profile/apply', { facilityId, ...(sheetNames ? { sheetNames } : {}) }),
  syncSessionKeys: (facilityId: string) =>
    httpClient.post<SyncSessionKeysResult>('/api/obs-profile/sync-session-keys', { facilityId }),
};

// ── Onboarding setup-status ──────────────────────────────────────────────────
export type SetupStepStatus = 'done' | 'todo' | 'blocked';

export interface SetupStep {
  key: string;
  label: string;
  status: SetupStepStatus;
  detail: string;
  counts?: Record<string, number>;
}

export interface SetupStatus {
  facilityId: string;
  name: string;
  steps: SetupStep[];
  completed: number;
  total: number;
  complete: boolean;
}

export interface SyncSessionKeysResult {
  facilityId: string;
  filled: number;
  overwritten: number;
  same: number;
  created: number;
  hostsUpdated: number;
  flags: string[];
}

export interface SetupOverviewItem {
  id: number;
  facilityId: string;
  name: string;
  isActive: boolean;
  completed: number;
  total: number;
  complete: boolean;
  steps: Array<{ key: string; label: string; status: SetupStepStatus }>;
}

export const setupApi = {
  getStatus: (facilityId: string) =>
    httpClient.get<SetupStatus>(`/api/admin/facilities/${facilityId}/setup-status`),
  getOverview: () =>
    httpClient.get<{ facilities: SetupOverviewItem[] }>('/api/admin/setup-overview'),
};

// ── Singular.live API (kept for reference, but removed from UI) ───────────────
export const singularApi = {
  // Get publish targets (for confirmation modal)
  getPublishTargets: (facilityId: string, sheetIds?: string[]): Promise<{
    facilityId: string;
    targets: Array<{
      sheetId: string;
      sheetName: string | null;
      appId: number;
      appToken: string; // Masked
      isActive: boolean;
      configured: boolean;
      uiOverwriteRisk: boolean;
    }>;
  }> => {
    const params: Record<string, unknown> = { facilityId };
    if (sheetIds && sheetIds.length > 0) {
      params['sheetIds'] = sheetIds;
    }
    return httpClient.get('/api/singular/publish-targets', params);
  },

  // Publish slideshow to Singular Control Apps
  publishSlideshow: (facilityId: string, templateId?: number, sheetIds?: string[]): Promise<{
    success: boolean;
    batchId: number;
    status: 'success' | 'partial' | 'failure';
    totalSheets: number;
    successCount: number;
    failureCount: number;
    targetedSheets: string[];
    attempts: Array<{
      sheetId: string;
      appId: number;
      appTokenMasked: string;
      success: boolean;
      httpStatus?: number;
      errorMessage?: string;
      retryCount: number;
      durationMs: number;
      timestamp: string;
      sentHash?: string;
      storedHash?: string;
      verifiedMatch?: boolean;
      uiOverwriteRisk: boolean;
    }>;
    durationMs: number;
  }> =>
    httpClient.post('/api/singular/publish-slideshow', {
      facilityId,
      templateId,
      sheetIds,
    }),

  // Get publish history
  getPublishHistory: (facilityId: string, limit = 50, offset = 0): Promise<{
    facilityId: string;
    history: Array<{
      batchId: number;
      facilityId: string;
      userId?: string;
      userEmail?: string;
      targetSheets: string[];
      totalSheets: number;
      successCount: number;
      failureCount: number;
      status: string;
      assetCount: number;
      totalDurationSec: number;
      payloadSizeBytes: number;
      slideshowHash?: string;
      createdAt: string;
      completedAt?: string;
      durationMs?: number;
      attempts: Array<{
        sheetId: string;
        success: boolean;
        httpStatus?: number;
        errorMessage?: string;
        retryCount: number;
        durationMs?: number;
        createdAt: string;
      }>;
    }>;
    limit: number;
    offset: number;
  }> =>
    httpClient.get('/api/singular/publish-history', { facilityId, limit, offset }),

  // Get single batch details
  getPublishBatch: (batchId: number): Promise<{
    batchId: number;
    facilityId: string;
    userId?: string;
    userEmail?: string;
    targetSheets: string[];
    totalSheets: number;
    successCount: number;
    failureCount: number;
    status: string;
    assetCount: number;
    totalDurationSec: number;
    payloadSizeBytes: number;
    slideshowHash?: string;
    createdAt: string;
    completedAt?: string;
    durationMs?: number;
    attempts: Array<{
      sheetId: string;
      success: boolean;
      httpStatus?: number;
      errorMessage?: string;
      errorCode?: string;
      retryCount: number;
      durationMs?: number;
      createdAt: string;
    }>;
    payload?: Array<{
      duration: number;
      payload: {
        Sponsor: string;
      };
    }>;
  }> =>
    httpClient.get(`/api/singular/publish-history/${batchId}`),

  // Singular App Management (Admin only)
  apps: {
    list: (facilityId: string): Promise<{
      apps: Array<{
        id: number;
        facilityId: string;
        sheetId: string;
        sheetName: string | null;
        appToken: string; // Masked
        subCompositionId: string;
        payloadKey: string;
        singularApiUrl: string;
        isActive: boolean;
        notes: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
    }> =>
      httpClient.get('/api/singular/apps', { facilityId }),

    create: (data: {
      facilityId: string;
      sheetId: string;
      sheetName?: string;
      appToken: string;
      subCompositionId?: string;
      payloadKey?: string;
      singularApiUrl?: string;
      notes?: string;
    }): Promise<{
      id: number;
      facilityId: string;
      sheetId: string;
      sheetName: string | null;
      appToken: string; // Masked
      subCompositionId: string;
      payloadKey: string;
      singularApiUrl: string;
      isActive: boolean;
      notes: string | null;
      createdAt: string;
      updatedAt: string;
    }> =>
      httpClient.post('/api/singular/apps', data),

    update: (id: number, data: {
      sheetId?: string;
      sheetName?: string;
      appToken?: string;
      subCompositionId?: string;
      payloadKey?: string;
      singularApiUrl?: string;
      isActive?: boolean;
      notes?: string;
    }): Promise<{
      id: number;
      facilityId: string;
      sheetId: string;
      sheetName: string | null;
      appToken: string; // Masked
      subCompositionId: string;
      payloadKey: string;
      singularApiUrl: string;
      isActive: boolean;
      notes: string | null;
      createdAt: string;
      updatedAt: string;
    }> =>
      httpClient.patch(`/api/singular/apps/${id}`, data),

    delete: (id: number): Promise<void> =>
      httpClient.delete(`/api/singular/apps/${id}`),
  },
};

// Global Settings API (admin only)
export const globalSettingsApi = {
  get: (): Promise<{ discordAlertsWebhook: string | null; discordActivityWebhook: string | null }> =>
    httpClient.get('/api/admin/settings'),

  update: (data: { discordAlertsWebhook?: string | null; discordActivityWebhook?: string | null }): Promise<{ discordAlertsWebhook: string | null; discordActivityWebhook: string | null }> =>
    httpClient.put('/api/admin/settings', data),

  testDiscord: (): Promise<{ results: Record<string, string> }> =>
    httpClient.post('/api/admin/settings/test-discord', {}),
};

// Query key factories for TanStack Query
export const queryKeys = {
  auth: {
    validate: ['auth', 'validate'] as const,
  },
  facilities: {
    all: ['facilities'] as const,
    lists: () => [...queryKeys.facilities.all, 'list'] as const,
    list: (params?: Record<string, unknown>) => [...queryKeys.facilities.lists(), params] as const,
    details: () => [...queryKeys.facilities.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.facilities.details(), id] as const,
  },
  logos: {
    all: ['logos'] as const,
    lists: () => [...queryKeys.logos.all, 'list'] as const,
    list: (facilityId: string) => [...queryKeys.logos.lists(), facilityId] as const,
  },
  templates: {
    all: ['templates'] as const,
    lists: () => [...queryKeys.templates.all, 'list'] as const,
    list: (params?: Record<string, unknown>) => [...queryKeys.templates.lists(), params] as const,
    details: () => [...queryKeys.templates.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.templates.details(), id] as const,
  },
  health: {
    check: ['health', 'check'] as const,
  },
  overlays: {
    all: ['overlays'] as const,
    compositions: (params?: Record<string, unknown>) => [...['overlays'], 'compositions', params] as const,
    composition: (id: number) => [...['overlays'], 'composition', id] as const,
  },
  colorSchemes: {
    all: ['colorSchemes'] as const,
    roles: () => [...['colorSchemes'], 'roles'] as const,
    list: (facilityId?: string) => [...['colorSchemes'], 'list', facilityId ?? '_all_'] as const,
    detail: (id: number) => [...['colorSchemes'], 'detail', id] as const,
  },
  schedules: {
    all: ['schedules'] as const,
    list: (params?: Record<string, unknown>) => [...['schedules'], 'list', params] as const,
    detail: (id: number) => [...['schedules'], 'detail', id] as const,
    blackouts: (params?: Record<string, unknown>) => [...['schedules'], 'blackouts', params] as const,
    rules: (facilityId?: string | null) => [...['schedules'], 'rules', facilityId ?? null] as const,
    agentStatus: (facilityId: string) => [...['schedules'], 'agentStatus', facilityId] as const,
    commands: (facilityId: string) => [...['schedules'], 'commands', facilityId] as const,
    fleetOverview: () => [...['schedules'], 'fleetOverview'] as const,
    scheduleHealth: () => [...['schedules'], 'scheduleHealth'] as const,
  },
  events: {
    all: ['events'] as const,
    list: (facilityId: string) => [...['events'], 'list', facilityId] as const,
    detail: (id: number) => [...['events'], 'detail', id] as const,
  },
  youtube: {
    all: ['youtube'] as const,
    credentials: (facilityId: string) => [...['youtube'], 'credentials', facilityId] as const,
    rule: (facilityId: string) => [...['youtube'], 'rule', facilityId] as const,
    logs: (params?: Record<string, unknown>) => [...['youtube'], 'logs', params] as const,
    streamKeys: (facilityId: string) => [...['youtube'], 'streamKeys', facilityId] as const,
  },
  globalSettings: {
    all: ['globalSettings'] as const,
  },
  setup: {
    status: (facilityId: string) => [...['setup'], 'status', facilityId] as const,
    overview: () => [...['setup'], 'overview'] as const,
  },
  adminAccounts: {
    all: ['adminAccounts'] as const,
    list: () => [...['adminAccounts'], 'list'] as const,
  },
  liveBroadcasts: {
    list: (facilityId: string) => [...['liveBroadcasts'], 'list', facilityId] as const,
  },
  metadataTemplates: {
    list: (facilityId: string) => [...['metadataTemplates'], 'list', facilityId] as const,
  },
} as const;
